import React, { useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * CoinBack - The reverse face of the C4sh3r coin.
 * Cyber design: circuit board frame + scannable QR + encrypted hash strip
 * + AUTHORIZED stamp. Sized via the `size` prop so it scales for header (sm),
 * hero (md) and standby (lg).
 */
const CoinBack = ({ size = 'lg', qrValue = 'https://github.com/C4sh3R' }) => {
  // Stable pseudo-hash so it doesn't shimmer between renders
  const hash = useMemo(() => {
    const chars = '0123456789abcdef';
    let h = '';
    for (let i = 0; i < 16; i += 1) h += chars[Math.floor(Math.random() * 16)];
    return h;
  }, []);

  if (size === 'sm') {
    // Tiny — no room for the full design; minimal cyber chip + "C4"
    return (
      <span className="coin-back-sm-wrap" aria-hidden="true">
        <span className="coin-back-circuit-mini" />
        <span className="coin-back-text">C4</span>
      </span>
    );
  }

  const qrSize = size === 'md' ? 56 : 78;

  return (
    <span className={`coin-back-content coin-back-${size}`} aria-hidden="true">
      {/* Circuit board background */}
      <svg
        className="coin-back-circuit"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <defs>
          <pattern id={`coin-pcb-${size}`} width="14" height="14" patternUnits="userSpaceOnUse">
            <path
              d="M0 7 H14 M7 0 V14 M0 0 L7 7 M14 0 L7 7 M0 14 L7 7 M14 14 L7 7"
              stroke="rgba(0,255,255,0.18)"
              strokeWidth="0.6"
              fill="none"
            />
            <circle cx="7" cy="7" r="0.9" fill="rgba(0,255,255,0.55)" />
          </pattern>
        </defs>
        <rect x="0" y="0" width="100" height="100" fill={`url(#coin-pcb-${size})`} />
        {/* Pulse traces */}
        <line x1="0" y1="20" x2="100" y2="20" className="coin-back-trace" />
        <line x1="0" y1="80" x2="100" y2="80" className="coin-back-trace coin-back-trace-2" />
        <line x1="20" y1="0"  x2="20" y2="100" className="coin-back-trace coin-back-trace-3" />
        <line x1="80" y1="0"  x2="80" y2="100" className="coin-back-trace coin-back-trace-4" />
      </svg>

      {/* Top label: hash strip */}
      <div className="coin-back-hash">
        <span className="coin-back-hash-label">SHA</span>
        <span className="coin-back-hash-value">{hash}</span>
      </div>

      {/* Center QR */}
      <div className="coin-back-qr-wrap">
        <span className="coin-back-qr-corner coin-back-qr-corner-tl" />
        <span className="coin-back-qr-corner coin-back-qr-corner-tr" />
        <span className="coin-back-qr-corner coin-back-qr-corner-bl" />
        <span className="coin-back-qr-corner coin-back-qr-corner-br" />
        <QRCodeSVG
          value={qrValue}
          size={qrSize}
          bgColor="rgba(0,8,16,0)"
          fgColor="#b06bff"
          level="L"
          marginSize={0}
        />
      </div>

      {/* Bottom: ID + AUTHORIZED stamp */}
      <div className="coin-back-footer">
        <span className="coin-back-id">C4SH3R // 0xC4</span>
        <span className="coin-back-stamp">AUTHORIZED</span>
      </div>
    </span>
  );
};

export default CoinBack;
